<template>
  <v-container>
    <v-row class="mx-1">
      <v-col class="ma-3">
        <h3 class="">
          Click on an existing user's name in the list below to:
        </h3>
        <ul class="ml-3">
          <li style="font-size: medium;">
            update their details
          </li>
          <li style="font-size: medium;">
            create deposit jobs for the user
          </li>
          <li style="font-size: medium;">
            view existing deposit jobs for the user
          </li>
        </ul>
      </v-col>
    </v-row>
    <v-row v-if="Array.isArray(users) && users.length">
      <v-col
        cols="12"
        class="px-4"
      >
        <v-text-field
          v-model="search"
          label="Search users"
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
        />
      </v-col>

      <v-col
        cols="12"
        class="px-4"
      >
        <v-data-table
          v-model:sort-by="sortBy"
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :items="tableItems"
          :search="search"
          :items-per-page-options="[5, 10, 25, 50]"
          :loading="loading"
          class="elevation-1"
          density="comfortable"
        >
          <template #[`item.name`]="{ item, value }">
            <a
              href="#"
              @click.prevent="selectUser(item.raw)"
            >{{ value }}</a>
          </template>

          <template #[`item.created`]="{ item }">
            {{ formatDate(item.raw.UserCreateDate) }}
          </template>

          <template #[`item.lastModified`]="{ item }">
            {{ formatDate(item.raw.UserLastModifiedDate) }}
          </template>

          <template #no-data>
            No users found.
          </template>
          <template #loading>
            <v-progress-linear indeterminate />
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row v-else>
      <p v-if="loading">
        Getting the list of users
      </p>
      <p v-else>
        No users found.
      </p>
    </v-row>

    <v-row>
      <v-dialog
        v-model="dialog"
        width="95%"
        @update:model-value="onDialogToggle"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            color="secondary"
            dark
            rounded
            class="ma-4"
            @click="startCreate"
          >
            <v-icon>mdi-plus</v-icon>
            <span title="Add user">Add user</span>
          </v-btn>
        </template>
        <v-card>
          <v-row
            class="ma-0 pa-4 admin-topbar"
            dense
          >
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-2">
                <h3>
                  <span v-if="selectedUser === null">Create user</span>
                  <span v-else>Edit user details</span>
                </h3>
                <v-btn
                  icon
                  variant="text"
                  density="compact"
                  aria-label="Close"
                  @click="dialog = false"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- UPDATE FORM (selected user) -->
              <v-form
                v-if="editingUser"
                ref="form"
                :key="'update-form'"
                v-model="valid"
                lazy-validation
              >
                <v-row
                  class="items-end"
                  dense
                >
                  <v-col
                    cols="12"
                    md="4"
                    lg="3"
                  >
                    <v-text-field
                      v-model="editingUser.email"
                      readonly
                      disabled
                      label="E-mail"
                      required
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                    lg="3"
                  >
                    <v-text-field
                      v-model="editingUser.name"
                      :counter="100"
                      :rules="nameRules"
                      label="Name"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                    lg="3"
                    class="text-right md:text-left"
                  >
                    <v-btn
                      :disabled="!valid"
                      color="secondary"
                      class="mt-2"
                      @click="updateUser()"
                    >
                      <v-icon class="mr-2">
                        mdi-content-save
                      </v-icon>
                      <span title="Update user">Update user</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>

              <!-- CREATE FORM (no selected user) -->
              <v-form
                v-else
                ref="form"
                :key="'create-form'"
                v-model="valid"
                lazy-validation
              >
                <v-row
                  class="items-end"
                  dense
                >
                  <v-col cols="12">
                    <v-alert
                      v-if="duplicateEmailError"
                      type="error"
                      density="compact"
                      class="mb-2"
                    >
                      {{ duplicateEmailError }}
                    </v-alert>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                    lg="3"
                  >
                    <v-text-field
                      v-model="email"
                      :rules="emailRules"
                      required
                    >
                      <template #label>
                        E-mail <span
                          class="text-error"
                          aria-hidden="true"
                        >*</span>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                    lg="3"
                  >
                    <v-text-field
                      v-model="name"
                      :counter="100"
                      :rules="createdNameRules"
                      required
                    >
                      <template #label>
                        Name <span
                          class="text-error"
                          aria-hidden="true"
                        >*</span>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                    lg="3"
                    class="text-right md:text-left"
                  >
                    <v-btn
                      :disabled="!valid"
                      color="secondary"
                      class="mt-2"
                      @click="createUser(email, name)"
                    >
                      <v-icon class="mr-2">
                        mdi-account-plus
                      </v-icon>
                      <span title="Create user">Create user</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </v-row>

          <!-- FULL-WIDTH CONTENT BELOW THE TOPBAR -->
          <v-row class="ma-0 px-4 pb-4">
            <v-col
              v-if="!(selectedUser === null)"
              cols="12"
            >
              <h3 class="mt-2">
                User's deposits
              </h3>
              <hr class="mb-2">
              <AdminDepositList
                :depositor="selectedUser"
                :admin-user-email="selectedUser?.email || ''"
                :admin-user-name="selectedUser?.name || selectedUser?.displayName || selectedUser?.email || ''"
                :admin-search="search"
                :admin-page="page"
              />
              <AdminCreateDeposits :depositor="selectedUser" />
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import config from "@/config"
import { AdminCreateUserCommand, AdminUpdateUserAttributesCommand, CognitoIdentityProviderClient } from "@aws-sdk/client-cognito-identity-provider";
import AdminCreateDeposits from '@/components/AdminCreateDeposits.vue'
import AdminDepositList from '@/components/AdminDepositList.vue'
import { formatDate } from '@/utils/formatDate';
import { ensureAdmin } from '@/utils/adminTools'

export default {
  name: "AdminUserList",
  components: {
    AdminCreateDeposits,
    AdminDepositList,
  },
  data: () => ({
    selectedUser: null,
    editingUser: null,
    dialog: false,
    valid: true,
    name: null,
    email: null,
    duplicateEmailError: '',
    nameRules: [
      v => (v && String(v).trim().length > 0) || 'Name is required',
      v => (!v || v.length <= 100) || 'Name must be less than 100 characters',
    ],
    emailRules: [
      v => !!v || 'E-mail is required',
      v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
    ],
    search: '',
    page: 1,
    itemsPerPage: 5,
    sortBy: [{ key: 'name', order: 'asc' }],
    headers: [
      { title: 'Name',          key: 'name',         sortable: true },
      { title: 'Email',         key: 'email',        sortable: true },
      { title: 'Created',       key: 'created',      sortable: true },
      { title: 'Last Modified', key: 'lastModified', sortable: true },
    ],
  }),
  computed:  {
    ...mapGetters({
      users: 'users/users',
      loading: 'users/loading'
    }),
    createdNameRules (){
      return [
        v => (v && String(v).trim().length > 0) || 'Name is required',
        v => (!v || v.length <= 100) || 'Name must be less than 100 characters',
      ];
    },
    tableItems() {
      const list = Array.isArray(this.users) ? this.users : []
      return list
      // filter out SAML users
      .filter(u => {
        const username = (u.Username || u.username || '').toString().toLowerCase()
        return !username.startsWith('raven-saml_')
      })
      .map(u => ({
        name: u.name || u.displayName || u.email || '—',
        email: u.email || '—',
        created: u.UserCreateDate,
        lastModified: u.UserLastModifiedDate,
        raw: u,
      }))
    },
  },
  watch: {
    users: {
      immediate: true,
      handler(newUsers) {
        const restoreEmail = this.$route.query.restore_user
        if (!restoreEmail || !newUsers?.length || this.dialog) return

        if (this.$route.query.admin_search !== undefined) {
          this.search = this.$route.query.admin_search
        }
        if (this.$route.query.admin_page) {
          this.page = parseInt(this.$route.query.admin_page, 10) || 1
        }

        const user = newUsers.find(u => {
          const username = (u.Username || u.username || '').toString().toLowerCase()
          return !username.startsWith('raven-saml_') && u.email === restoreEmail
        })
        if (user) {
          this.selectUser(user)
        }
        this.$router.replace({ ...this.$route, query: { ...this.$route.query, restore_user: undefined, admin_search: undefined, admin_page: undefined } })
      }
    }
  },
  created() {
    this.$store.dispatch('users/getUsers')
  },
  methods: {
    ...mapActions([
      'getUsers',
    ]),
    async makeCognitoClient() {
      const creds = await this.$cognitoAuth.getAwsCredentials();
      const poolRegion =
        (typeof config.userPoolId === 'string' && config.userPoolId.includes('_'))
          ? config.userPoolId.split('_')[0]
          : (config.region || 'eu-west-1');

      return new CognitoIdentityProviderClient({
        region: poolRegion,
        credentials: {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          sessionToken: creds.sessionToken,
        },
      });
    },
    formatDate,
    selectUser(user) {
      this.selectedUser = user
      this.editingUser = { ...user }
      this.dialog = true
      console.log(this.selectedUser)
    },
    onDialogToggle(val) {
      if (!val) this.resetForms();
    },
    resetForms () {
      this.$refs.form?.resetValidation()
      this.selectedUser = null
      this.editingUser = null
      this.name = ''
      this.email = ''
    },
    async updateUser() {
      if (!(await ensureAdmin(this.$cognitoAuth, '105877'))) {
        console.warn('Not an admin; blocking admin API call');
        return;
      }
      if (this.$refs.form.validate()) {
        const client = await this.makeCognitoClient();

        const user = this.selectedUser;      // original
        const updated = this.editingUser;    // edited fields

        const Username = user.Username ?? user.sub ?? user.email;
        const params = {
          UserPoolId: config.userPoolId,
          Username,
          UserAttributes: [{ Name: 'name', Value: updated.name }],
        };
        try {
          await client.send(new AdminUpdateUserAttributesCommand(params));
          this.dialog = false
          this.$store.dispatch('users/getUsers')
        } catch (err) {
          console.log(err, err.stack)
        }
      }
    },
    async createDepositForUser(user) {
      if (this.$refs.form.validate()) {
        const creds = await this.$cognitoAuth.getAwsCredentials();
        const poolRegion =
          (typeof config.userPoolId === 'string' && config.userPoolId.includes('_'))
            ? config.userPoolId.split('_')[0]
            : (config.region || 'eu-west-1');
        const client = new CognitoIdentityProviderClient({
          region: poolRegion,
          credentials: {
            accessKeyId: creds.accessKeyId,
            secretAccessKey: creds.secretAccessKey,
            sessionToken: creds.sessionToken,
          },
        });
        console.log("user's identityid: " + JSON.stringify(user["custom:identityid"]))
        console.log(client)
      }
    },
    async createUser(email, name) {
      if (!(await ensureAdmin(this.$cognitoAuth, '105877'))) {
        console.warn('Not an admin; blocking admin API call');
        return;
      }
      const emailTrim = (email || '').trim();
      const nameTrim  = (name  || '').trim();

      // clear any previous duplicate warning
      this.duplicateEmailError = '';

      if (!emailTrim || !nameTrim) {
        // trigger form validation feedback
        this.$refs.form?.validate();
        return;
      }

      const existingUser = (Array.isArray(this.users) ? this.users : []).some(u =>
        (u.email || '').trim().toLowerCase() === emailTrim.toLowerCase()
      );

      if (existingUser) {
        this.duplicateEmailError = `A user with the e-mail ${emailTrim} already exists.`;
        return; // don't call Cognito
      }

      if (this.$refs.form.validate()) {
        const client = await this.makeCognitoClient();
        const params = {
          UserPoolId: config.userPoolId,
          Username: emailTrim,
          UserAttributes: [
            { Name: 'email', Value: emailTrim },
            { Name: 'name',  Value: nameTrim },
          ],
          MessageAction: 'SUPPRESS',
        };
        try {
          await client.send(new AdminCreateUserCommand(params));

          // hard reset form state now
          this.$refs.form?.reset();
          this.$refs.form?.resetValidation();
          this.name = '';
          this.email = '';
          
          this.dialog = false;
          this.$store.dispatch('users/getUsers');
        } catch (err) {
          console.log(err, err.stack);
        }
      }
    },
    startCreate() {
      this.selectedUser = null;
      this.name = '';
      this.email = '';
      this.$nextTick(() => {
        this.$refs.form?.reset();            // clears inputs
        this.$refs.form?.resetValidation();  // clears messages
      });
    },
  }
}
</script>

<style scoped>
.admin-topbar :deep(.v-field) {
  /* make the fields a bit more compact to save vertical space */
  --v-input-padding-top: 6px;
  --v-input-padding-bottom: 6px;
}

.admin-topbar h3 {
  font-size: 1.1rem;
  margin: 0;
}

@media (min-width: 960px) {
  /* help align the action button vertically with fields on md+ */
  .admin-topbar .v-btn {
    margin-top: 8px;
  }
}
</style>